import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import SimpleFooter from '../components/SimpleFooter';
import ChatFAB from '../components/ChatFAB';
import Loader from '../components/Loader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packageData, setPackageData] = useState(null);
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState({ whatsappNumber: '+919876543210' });

  useEffect(() => {
    // Fetch site settings
    fetch(`${API_URL}/api/site-settings`)
      .then(res => res.json())
      .then(settings => {
        setSiteSettings(settings);
      })
      .catch(err => console.error('Error fetching site settings:', err));

    // Fetch all packages to find the one we need
    fetch(`${API_URL}/api/admin/packages`)
      .then(res => res.json())
      .then(packages => {
        const pkg = packages.find(p => p.id === id);
        if (pkg) {
          setPackageData(pkg);
          // Fetch destination details
          fetch(`${API_URL}/api/destinations`)
            .then(res => res.json())
            .then(destinations => {
              const dest = destinations.find(d => d.id === pkg.destinationId);
              setDestination(dest);
              setLoading(false);
            })
            .catch(err => {
              console.error('Error fetching destination:', err);
              setLoading(false);
            });
        } else {
          navigate('/destinations');
        }
      })
      .catch(err => {
        console.error('Error fetching package:', err);
        setLoading(false);
        navigate('/destinations');
      });
  }, [id, navigate]);

  if (loading) {
    return (
      <>
        <Header />
        <Loader fullPage={true} />
        <SimpleFooter />
      </>
    );
  }

  if (!packageData || !destination) {
    return null;
  }

  const getDestinationSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-');
  };

  return (
    <>
      <Header />
      <div className="package-detail-page">
        <div className="package-detail-hero" style={{ backgroundImage: `url(${packageData.image})` }}>
          <div className="package-detail-overlay"></div>
          <div className="package-detail-hero-content">
            <Link to={`/destinations/${getDestinationSlug(destination.name)}`} className="back-to-destination">
              ← Back to {destination.name}
            </Link>
            <h1>{packageData.title}</h1>
            <div className="package-hero-meta">
              <span className="package-hero-duration">
                <i className="fa-regular fa-clock"></i> {packageData.duration}
              </span>
              <span className="package-hero-destination">
                <i className="fa-solid fa-location-dot"></i> {destination.name}
              </span>
            </div>
          </div>
        </div>

        <div className="package-detail-container">
          <div className="package-detail-content">
            <div className="package-main-content">
              <section className="package-detail-section">
                <h2>Package Overview</h2>
                <p className="package-detail-description">{packageData.description}</p>
              </section>

              {packageData.inclusions && packageData.inclusions.length > 0 && (
                <section className="package-detail-section">
                  <h2><i className="fa-solid fa-check-circle"></i> What's Included</h2>
                  <ul className="package-detail-list inclusions">
                    {packageData.inclusions.map((item, idx) => (
                      <li key={idx}>
                        <i className="fa-solid fa-check"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {packageData.exclusions && packageData.exclusions.length > 0 && (
                <section className="package-detail-section">
                  <h2><i className="fa-solid fa-times-circle"></i> What's Not Included</h2>
                  <ul className="package-detail-list exclusions">
                    {packageData.exclusions.map((item, idx) => (
                      <li key={idx}>
                        <i className="fa-solid fa-times"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {packageData.itinerary && packageData.itinerary.length > 0 && (
                <section className="package-detail-section">
                  <h2><i className="fa-solid fa-route"></i> Day-by-Day Itinerary</h2>
                  <div className="itinerary-timeline">
                    {packageData.itinerary.map((day, idx) => (
                      <div key={idx} className="itinerary-day">
                        <div className="itinerary-day-number">Day {day.day}</div>
                        <div className="itinerary-day-content">
                          <h3>{day.title}</h3>
                          <p>{day.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <div className="package-sidebar">
              <div className="package-price-card">
                <div className="package-price-header">
                  {packageData.featured && (
                    <span className="package-featured-tag">
                      <i className="fa-solid fa-star"></i> Featured Package
                    </span>
                  )}
                  <div className="package-price-display">
                    {packageData.originalPrice && (
                      <span className="package-price-original">₹{packageData.originalPrice}</span>
                    )}
                    <span className="package-price-current">₹{packageData.price}</span>
                    <span className="package-price-label">per person</span>
                  </div>
                </div>

                <div className="package-quick-info">
                  <div className="quick-info-item">
                    <i className="fa-regular fa-clock"></i>
                    <div>
                      <span className="quick-info-label">Duration</span>
                      <span className="quick-info-value">{packageData.duration}</span>
                    </div>
                  </div>
                  <div className="quick-info-item">
                    <i className="fa-solid fa-location-dot"></i>
                    <div>
                      <span className="quick-info-label">Destination</span>
                      <span className="quick-info-value">{destination.name}</span>
                    </div>
                  </div>
                </div>

                <a 
                  href={`https://wa.me/${siteSettings.whatsappNumber.replace('+', '')}?text=Hi, I'm interested in the ${packageData.title} package for ${destination.name}. Duration: ${packageData.duration}, Price: ₹${packageData.price}. Please provide more details and help me book this package.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="package-book-btn"
                >
                  <i className="fa-brands fa-whatsapp"></i> Book Now on WhatsApp
                </a>

                <p className="package-help-text">
                  Have questions? Our travel experts are here to help you plan the perfect trip!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SimpleFooter />
      <ChatFAB />
    </>
  );
};

export default PackageDetail;
